import { Loader2Icon, Maximize2Icon } from 'lucide-react';
import { useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Blob } from '@/services/types/blob-types';

interface PreviewSheetProps {
  file: Blob;
}

export const PreviewSheet = ({ file }: PreviewSheetProps) => {
  const [isPreviewLoading, setIsPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Stabilize the download URL so that polling-induced presigned URL changes
  // don't cause the iframe/image to reload and reset the user's scroll position.
  // Only update when the file itself changes (different ID).
  const stableFileIdRef = useRef(file.id);
  const stableDownloadUrlRef = useRef(file.download_url);
  if (file.id !== stableFileIdRef.current) {
    stableFileIdRef.current = file.id;
    stableDownloadUrlRef.current = file.download_url;
  }

  const mimeType = file.file_mime_type || 'application/octet-stream';
  const isPreviewable =
    mimeType.startsWith('image/') ||
    mimeType === 'application/pdf' ||
    mimeType.includes('text/');

  const handlePreviewLoad = () => {
    setIsPreviewLoading(false);
  };

  const handlePreviewError = () => {
    setIsPreviewLoading(false);
    setPreviewError('Failed to load preview');
  };

  if (!isPreviewable) {
    return null;
  }

  return (
    <Sheet>
      <Tooltip>
        <TooltipTrigger>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0 rounded-sm transition-all hover:bg-muted/50 active:scale-95"
              aria-label="Open preview"
            >
              <Maximize2Icon className="h-3.5 w-3.5" />
            </Button>
          </SheetTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-[10px] py-1 px-2">
          Open preview
        </TooltipContent>
      </Tooltip>
      <SheetContent side="left" className="w-1/2 sm:max-w-none flex flex-col">
        <SheetHeader>
          <SheetTitle className="text-base">{file.name}</SheetTitle>
        </SheetHeader>
        <div className="relative bg-muted rounded-md overflow-hidden mt-4 flex-1">
          {isPreviewLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <Loader2Icon className="h-8 w-8 animate-spin text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  Loading preview...
                </span>
              </div>
            </div>
          )}

          {previewError && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <span className="text-sm text-destructive">{previewError}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsPreviewLoading(true);
                    setPreviewError(null);
                    // Force reload with the latest URL from the prop
                    stableDownloadUrlRef.current = file.download_url;
                    setTimeout(() => {
                      const iframe = document.querySelector('iframe');
                      if (iframe) iframe.src = file.download_url;
                    }, 100);
                  }}
                >
                  Retry
                </Button>
              </div>
            </div>
          )}

          {mimeType.startsWith('image/') ? (
            <img
              src={stableDownloadUrlRef.current}
              alt={file.name}
              className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-200 ${
                isPreviewLoading ? 'opacity-0' : 'opacity-100'
              }`}
              onLoad={handlePreviewLoad}
              onError={handlePreviewError}
            />
          ) : (
            <iframe
              src={stableDownloadUrlRef.current}
              className="absolute inset-0 w-full h-full"
              title={file.name}
              allow="fullscreen"
              onLoad={handlePreviewLoad}
              onError={handlePreviewError}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PreviewSheet;
